<template>
  <header class="main-header" :class="{ 'nav-open': isMobileMenuOpen }">
      <nav class="nav-container">
          <router-link to="/" class="logo-wrapper">
              <img src="assets/img/cyanlogo.png" alt="CYAN WATERPARK" class="logo">
          </router-link>

          <button class="mobile-toggle" @click="toggleMobileMenu">
              <span></span><span></span><span></span>
          </button>

          <div class="nav-menu" :class="{ 'active': isMobileMenuOpen }">
              <router-link to="/park-rules" class="nav-item">{{ $t('header.rules') }}</router-link>

              <div class="dropdown">
                  <button class="nav-item dropdown-toggle rides-link" @click="toggleDropdown('rides')">
                      {{ $t('header.rides') }}
                      <i :class="{ 'rotate': activeDropdown === 'rides' }"></i>
                  </button>
                  <div class="dropdown-menu rides-dropdown" :class="{ 'show': activeDropdown === 'rides' }">
                      <router-link to="/rides/family" class="dropdown-item">{{ $t('header.family_rides') }}</router-link>
                      <router-link to="/rides/thrill" class="dropdown-item">{{ $t('header.thrill_rides') }}</router-link>
                      <router-link to="/rides/kids" class="dropdown-item">{{ $t('header.kids_rides') }}</router-link>
                  </div>
              </div>
              <router-link to="/dress-code" class="nav-item">{{ $t('header.dress_code') }}</router-link>
              <router-link to="/school-trip" class="nav-item">{{ $t('header.school_trips') }}</router-link>
              <router-link to="/faqs" class="nav-item">{{ $t('header.faqs') }}</router-link>
          </div>

          <div class="right-items">
              <button class="language-switch" @click="switchLanguage">
                  <i class="fas fa-language"></i>
                  {{ currentLanguage === 'en' ? 'عربى' : 'En' }}
              </button>

              <!-- User Menu Dropdown -->
              <div v-if="userStore.isLoggedIn()" class="user-dropdown">
                  <button class="user-menu-btn" @click="toggleDropdown('user')">
                      <div class="user-avatar">
                          <img v-if="userStore.user?.profile_image" :src="userStore.user.profile_image" alt="Profile">
                          <i v-else class="fas fa-user"></i>
                      </div>
                      <span class="user-name">{{ userStore.user?.first_name }}</span>
                      <i class="fas fa-chevron-down" :class="{ 'rotate': activeDropdown === 'user' }"></i>
                  </button>
                  <div class="dropdown-menu user-dropdown-menu" :class="{ 'show': activeDropdown === 'user' }">
                      <router-link to="/profile" class="dropdown-item">
                          <i class="fas fa-user"></i> {{ $t('header.my_profile') }}
                      </router-link>
                      <router-link to="/my-tickets" class="dropdown-item">
                          <i class="fas fa-ticket-alt"></i> {{ $t('header.my_tickets') }}
                      </router-link>
                      <router-link to="/my-passes" class="dropdown-item">
                          <i class="fas fa-id-card"></i> {{ $t('header.my_passes') }}
                      </router-link>
                      <button @click="doUserLogout" class="dropdown-item logout-btn">
                          <i class="fas fa-sign-out-alt"></i> {{ $t('header.logout') }}
                      </button>
                  </div>
              </div>

              <!-- Login Button for Non-logged in Users -->
              <router-link v-else to="/login" class="buy-tickets-btn">
                  {{ $t('header.login') }}
              </router-link>
          </div>
      </nav>
  </header>
</template>

<script setup>
import { useSettingStore } from '@/stores/settingStore.js';
import { useUserStore } from '@/stores/userStore';
import { ref, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

const userStore = useUserStore();
const { locale } = useI18n();
const router = useRouter();
const isMobileMenuOpen = ref(false);
const activeDropdown = ref(null);
const currentLanguage = ref(locale.value);
const settingStore = useSettingStore();

const doUserLogout = () => {
  userStore.logout();
  router.push('/');
};



const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

const toggleDropdown = (dropdownName) => {
  activeDropdown.value = activeDropdown.value === dropdownName ? null : dropdownName;
};

const switchLanguage = () => {
  const newLanguage = locale.value === 'en' ? 'ar' : 'en';
  locale.value = newLanguage;
  currentLanguage.value = newLanguage;
  settingStore.setLanguage(newLanguage);
  document.documentElement.setAttribute('dir', newLanguage === 'ar' ? 'rtl' : 'ltr');
};

router.afterEach(() => {
  activeDropdown.value = null;
  isMobileMenuOpen.value = false;
});

const handleOutsideClick = (event) => {
  const dropdowns = document.querySelectorAll('.dropdown-menu.show, .dropdown-toggle, .user-menu-btn');
  const isClickInside = Array.from(dropdowns).some((dropdown) =>
    dropdown.contains(event.target)
  );

  if (!isClickInside) {
    activeDropdown.value = null;
  }
};

onMounted(() => {
  document.addEventListener('click', handleOutsideClick);
});

onUnmounted(() => {
  document.removeEventListener('click', handleOutsideClick);
});

watch(locale, (newLocale) => {
  document.documentElement.setAttribute('dir', newLocale === 'ar' ? 'rtl' : 'ltr');
});
</script>

<style scoped>
.main-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
}

.nav-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 15px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo {
  height: 50px;
  width: auto;
}

.nav-menu {
  display: flex;
  align-items: center;
  gap: 30px;
}

.nav-item {
  color: #333;
  text-decoration: none;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.nav-item:hover {
  background-color: rgba(239, 176, 54, 0.1);
  color: #EFB036;
}

/* Dropdown Styles */
.dropdown {
  position: relative;
}

.dropdown-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  border: none;
  background: none;
  font-size: inherit;
}

.dropdown-toggle i {
  transition: transform 0.3s ease;
}

.dropdown-toggle i.rotate {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  padding: 8px 0;
  min-width: 200px;
  opacity: 0;
  visibility: hidden;
  transform: translateY(10px);
  transition: all 0.3s ease;
}

.dropdown-menu.show {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.dropdown-item {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  color: #333;
  text-decoration: none;
  transition: background-color 0.3s;
}

.dropdown-item:hover {
  background-color: rgba(239, 176, 54, 0.1);
  color: #EFB036;
}

/* Right Items */
.right-items {
  display: flex;
  align-items: center;
  gap: 20px;
}

.language-switch {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  padding: 8px;
  color: #333;
  display: flex;
  align-items: center;
  gap: 8px;
}

.buy-tickets-btn {
  background: #EFB036;
  color: white;
  padding: 10px 20px;
  border-radius: 25px;
  text-decoration: none;
  font-weight: 600;
  transition: background-color 0.3s;
}

.buy-tickets-btn:hover {
  background: #cc0070;
}

/* User Menu Styles */
.user-dropdown {
  position: relative;
}

.user-menu-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 25px;
  transition: background-color 0.3s;
}

.user-menu-btn:hover {
  background-color: rgba(239, 176, 54, 0.1);
}

.user-avatar {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  overflow: hidden;
  background: #EFB036;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-name {
  font-weight: 600;
  color: #333;
}

.user-dropdown-menu {
  right: 0;
  left: auto;
}

.user-dropdown-menu .dropdown-item i {
  width: 20px;
  text-align: center;
  margin-right: 10px;
}

.logout-btn {
  width: 100%;
  text-align: left;
  padding: 12px 20px;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: #dc3545;
}

.logout-btn:hover {
  background-color: rgba(220, 53, 69, 0.1);
}

.logout-btn i {
  width: 20px;
  text-align: center;
  margin-right: 10px;
}

/* Mobile Menu */
.mobile-toggle {
  display: none;
  flex-direction: column;
  gap: 6px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
}

.mobile-toggle span {
  display: block;
  width: 25px;
  height: 2px;
  background-color: #333;
  transition: transform 0.3s ease;
}

/* Mobile Responsive */
@media (max-width: 991px) {
  .mobile-toggle {
    display: flex;
  }

  .nav-menu {
    position: fixed;
    top: 80px;
    left: 0;
    right: 0;
    background: white;
    flex-direction: column;
    padding: 20px;
    gap: 15px;
    transform: translateY(-100%);
    opacity: 0;
    visibility: hidden;
    transition: all 0.3s ease;
  }

  .nav-menu.active {
    transform: translateY(0);
    opacity: 1;
    visibility: visible;
  }

  .rides-dropdown {
    position: static;
    box-shadow: none;
    border: 1px solid #eee;
    margin-top: 10px;
  }

  .user-dropdown-menu {
    position: absolute;
    width: 220px;
  }
}

/* RTL Support */
[dir="rtl"] .dropdown-menu {
  left: auto;
  right: 0;
}

[dir="rtl"] .user-dropdown-menu {
  left: 0;
  right: auto;
}

[dir="rtl"] .dropdown-item i {
  margin-right: 0;
  margin-left: 10px;
}

[dir="rtl"] .logout-btn {
  text-align: right;
}

[dir="rtl"] .user-menu-btn {
  flex-direction: row-reverse;
}
</style>